/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { Link } from '@toucan/client/shared/components/link';
import { useWindowResizeCallback } from '@toucan/client/shared/hooks/use-window-resize';

import { getExpanderLabel } from './helpers';
import { styles } from './truncate-text.styles';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type ExpanderLabels = {
    seeMore?: string;
    seeLess?: string;
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type TruncateTextProps = {
    isLoading?: boolean;
    children?: ReactNode;
    'data-testid'?: string;
    className?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    hideExpander?: boolean;

    /**
     * The number of lines to show
     *
     * @default 1
     */
    lines?: number;

    /**
     * The labels for the expander
     */
    expanderLabels?: ExpanderLabels;

    /**
     * Whether the text should initially be truncated
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    initialTruncateText?: boolean;
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledTruncateTextProps = Pick<TruncateTextProps, 'lines'> & {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    truncateText: boolean;
};

const StyledTruncateText = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const isElementOverflowed = (element: HTMLElement) => {
    // apply -webkit-box to evaluate if element has overflowed when truncated
    element.setAttribute('style', 'display: -webkit-box');

    const { clientWidth, clientHeight, scrollWidth, scrollHeight } = element;

    element.setAttribute('style', '');

    return scrollHeight > clientHeight || scrollWidth > clientWidth;
};

export const TruncateText: FunctionComponent<TruncateTextProps> = ({
    isLoading = false,
    lines = 1,
    expanderLabels,
    hideExpander = false,
    initialTruncateText = true,
    children,
    'data-testid': dataTestId = 'truncate-text',
    ...otherProps
}: TruncateTextProps) => {
    const { t } = useTranslation();
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const [truncateText, setTruncateText] =
        useState<boolean>(initialTruncateText);
    const textContainerRef = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    const shouldShowExpander = Boolean(isOverflowed && !hideExpander);

    const checkElementOverflow = () => {
        if (textContainerRef.current) {
            // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
            const overflowed = isElementOverflowed(textContainerRef.current);

            if (overflowed !== isOverflowed) {
                setIsOverflowed(overflowed);
            }
        }
    };

    useWindowResizeCallback(checkElementOverflow);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    useEffect(checkElementOverflow, [isLoading]);

    const renderTextContainer = () => {
        if (isLoading) {
            return (
                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={lines}
                    width="100%"
                    className="text-container skeleton"
                    data-testid="truncate-text__text-container_skeleton"
                />
            );
        }

        return (
            <div
                className="text-container"
                data-testid="truncate-text__text-container"
                ref={textContainerRef}
            >
                {children}
            </div>
        );
    };

    const renderExpander = () => {
        if (!shouldShowExpander) {
            return null;
        }

        if (isLoading) {
            return (
                <Skeleton
                    variant={SkeletonVariant.TEXT}
                    lineCount={1}
                    width="10rem"
                    data-testid="truncate-text__expander_skeleton"
                    className="expander skeleton"
                />
            );
        }

        return (
            <Link
                onClick={() => setTruncateText(!truncateText)}
                data-testid="truncate-text__expander"
                className="expander"
            >
                {getExpanderLabel({ t, truncateText, expanderLabels })}
            </Link>
        );
    };

    return (
        <StyledTruncateText
            truncateText={truncateText}
            lines={lines}
            data-testid={dataTestId}
            {...otherProps}
        >
            {renderTextContainer()}
            {renderExpander()}
        </StyledTruncateText>
    );
};
